import { Link } from 'react-router-dom'
import { ArrowRight, Play } from 'lucide-react'
import { HeroSlider } from '@/components/sections/HeroSlider'
import { Scoreboard } from '@/components/brand/Scoreboard'
import { ProgramsGrid } from '@/components/sections/ProgramsGrid'
import { StatsStrip } from '@/components/sections/StatsStrip'
import { CampsGrid } from '@/components/sections/CampsGrid'
import { GalleryMasonry } from '@/components/sections/GalleryMasonry'
import { JamaicaMap } from '@/components/sections/JamaicaMap'
import { VideoReel } from '@/components/sections/VideoReel'
import { ImageCarousel } from '@/components/sections/ImageCarousel'
import { TestimonialsMarquee } from '@/components/sections/TestimonialsMarquee'
import { SponsorsStrip } from '@/components/sections/SponsorsStrip'
import { Button } from '@/components/ui/button'
import { useStats, usePrograms, useCamps, useGallery, useJamaicaCoaches, useClub, useTestimonials, useCoaches, useVideos, useSponsors } from '@/lib/hooks'
import { Reveal } from '@/components/motion/Reveal'
import { CosmosCrest } from '@/components/brand/CosmosCrest'

export function HomePage() {
  const { data: club } = useClub()
  const { data: stats } = useStats()
  const { data: programs = [] } = usePrograms()
  const { data: camps = [] } = useCamps()
  const { data: gallery = [] } = useGallery()
  const { data: jamaica = [] } = useJamaicaCoaches()
  const { data: testimonials = [] } = useTestimonials()
  const { data: coaches = [] } = useCoaches()
  const { data: videos = [] } = useVideos()
  const { data: sponsorData } = useSponsors()

  return (
    <>
      <HeroSlider />

      {stats && <Scoreboard match={stats.latestMatch} />}

      {/* Mission / About preview */}
      {club && (
        <section className="py-24">
          <div className="container-narrow grid gap-12 md:grid-cols-2 items-center">
            <Reveal>
              <span className="eyebrow">The True Spirit of Football</span>
              <h2 className="mt-4 font-display text-display-3 uppercase leading-none">
                Developing players.<br />Building citizens.
              </h2>
              <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{club.mission}</p>
              <p className="mt-4 text-base text-cosmos-green italic">"{club.quotes[0]}"</p>
              <Button asChild className="mt-6"><Link to="/about">Our Story <ArrowRight className="h-4 w-4" /></Link></Button>
            </Reveal>
            <Reveal delay={0.15}>
              <ImageCarousel
                aspect="4/5"
                intervalMs={4500}
                images={[
                  { src: 'https://cosmosfc.org/wp-content/uploads/2022/03/S6-1024x670.jpg', caption: 'Team celebrations, Vista CA' },
                  { src: 'https://cosmosfc.org/wp-content/uploads/2022/03/jj07-1024x682.jpg', caption: 'Elite match day' },
                  { src: 'https://cosmosfc.org/wp-content/uploads/2022/03/Z4-1024x768.jpg', caption: 'Academy training session' },
                  { src: 'https://cosmosfc.org/wp-content/uploads/2022/03/CM8-1.jpg', caption: 'Advance program work' },
                ]}
              />
            </Reveal>
          </div>
        </section>
      )}

      {/* Programs */}
      <section className="py-24 bg-muted/40">
        <div className="container-narrow">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
            <div>
              <span className="eyebrow">Player Pathways</span>
              <h2 className="mt-3 font-display text-display-3 uppercase">Programs for every level.</h2>
            </div>
            <p className="max-w-md text-muted-foreground">
              Ten tiered programs — from first touches to Spain tours. Each pathway is built around consistent coaching, real minutes, and a clear next step.
            </p>
          </div>
          <ProgramsGrid programs={programs} />
          <div className="mt-8 flex justify-center">
            <Button asChild variant="outline"><Link to="/programs">View all programs <ArrowRight className="h-4 w-4" /></Link></Button>
          </div>
        </div>
      </section>

      <StatsStrip />

      {/* Meet the Coaches */}
      {coaches.length > 0 && (
        <section className="py-24">
          <div className="container-narrow">
            <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
              <div>
                <span className="eyebrow">Staff &amp; Coaching</span>
                <h2 className="mt-3 font-display text-display-3 uppercase">Meet the coaches.</h2>
              </div>
              <Link to="/coaches" className="text-sm font-bold text-cosmos-green uppercase tracking-wide hover:underline flex items-center gap-1">
                View all staff <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {coaches.map((c, i) => (
                <Reveal key={c.slug} delay={i * 0.08}>
                  <Link to="/coaches" className="block group">
                    <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-pitch-800 relative">
                      <img
                        src={c.image}
                        alt={c.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-pitch-950/95 via-pitch-950/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <div className="text-[10px] uppercase tracking-widest text-cosmos-gold">{c.role}</div>
                        <div className="font-display text-xl uppercase leading-tight mt-1">{c.name}</div>
                        {c.licenses.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {c.licenses.slice(0, 2).map((l) => (
                              <span key={l} className="rounded bg-white/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest">
                                {l}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Video section — real MP4s from soccerclipboards.com */}
      {videos.length > 0 && (
        <section className="py-24 bg-pitch-950 text-white overflow-hidden">
          <div className="container-narrow">
            <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
              <div>
                <span className="eyebrow eyebrow-gold">Watch Cosmos in action</span>
                <h2 className="mt-3 font-display text-display-3 uppercase">Training reels &amp; match footage.</h2>
                <p className="mt-4 text-white/70 max-w-xl">
                  Real training sessions and match clips from the pitch — courtesy of the Soccer Clipboards Academy archive.
                </p>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Button asChild variant="gold">
                  <Link to="/gallery"><Play className="h-4 w-4" /> All videos &amp; photos</Link>
                </Button>
              </div>
            </div>
            <VideoReel videos={videos} layout="row" />
          </div>
        </section>
      )}

      {/* Camps */}
      <section className="py-24">
        <div className="container-narrow">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
            <div>
              <span className="eyebrow">Seasonal &amp; Year-Round</span>
              <h2 className="mt-3 font-display text-display-3 uppercase">Camps for every calendar.</h2>
            </div>
            <p className="max-w-md text-muted-foreground">Four camp formats scheduled throughout the year. Open to Cosmos members and outside players.</p>
          </div>
          <CampsGrid camps={camps} />
        </div>
      </section>

      {/* Jamaica */}
      <section className="py-24 bg-pitch-900 text-white">
        <div className="container-narrow">
          <div className="grid gap-10 md:grid-cols-2 items-center mb-10">
            <Reveal>
              <span className="eyebrow eyebrow-gold">Cosmos F.C. Jamaica</span>
              <h2 className="mt-3 font-display text-display-3 uppercase">Teaching the game the right way — on the island.</h2>
              <p className="mt-5 text-white/75 leading-relaxed">
                The Jamaica Development Recreation Football Program brings the Cosmos methodology to youth players ages 6-12 across Jamaica's parishes. Regional coaches, fun-first curriculum, personal growth alongside skill.
              </p>
              <Button asChild variant="gold" className="mt-6"><Link to="/programs/jamaica">Program details <ArrowRight className="h-4 w-4" /></Link></Button>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="grid place-items-center">
                <CosmosCrest size={220} animated />
              </div>
            </Reveal>
          </div>
          <JamaicaMap coaches={jamaica} />
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 bg-muted/40">
        <div className="container-narrow">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
            <div>
              <span className="eyebrow">Moments From The Pitch</span>
              <h2 className="mt-3 font-display text-display-3 uppercase">Gallery.</h2>
            </div>
            <Link to="/gallery" className="text-sm font-bold text-cosmos-green uppercase tracking-wide hover:underline flex items-center gap-1">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <GalleryMasonry items={gallery.slice(0, 8)} />
        </div>
      </section>

      {/* Testimonials — marquee */}
      {testimonials.length > 0 && (
        <section className="py-24">
          <div className="container-narrow">
            <div className="mb-10 text-center">
              <span className="eyebrow justify-center">What families say</span>
              <h2 className="mt-3 font-display text-display-3 uppercase">Voices from the Cosmos family.</h2>
            </div>
          </div>
          <TestimonialsMarquee testimonials={testimonials} />
        </section>
      )}

      {/* Sponsors band */}
      {sponsorData && (
        <section className="py-16 bg-muted/40 border-y border-border">
          <div className="container-narrow">
            <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
              <div>
                <span className="eyebrow">Partners &amp; Alliances</span>
                <h2 className="mt-2 font-display text-3xl uppercase">Trusted by</h2>
              </div>
              <Link to="/sponsors" className="text-sm font-bold text-cosmos-green uppercase tracking-wide hover:underline">
                All partners →
              </Link>
            </div>
            <SponsorsStrip sponsors={sponsorData.sponsors} />
          </div>
        </section>
      )}
    </>
  )
}
